import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload } from "lucide-react";

interface LoanNoteFormProps {
  lenderProfileId: string;
  loanNote?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

export const LoanNoteForm = ({ lenderProfileId, loanNote, onSuccess, onCancel }: LoanNoteFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [formData, setFormData] = useState({
    loan_from: "",
    loan_to: "",
    loan_signed_on: "",
    loan_agreement_url: "",
    total_funds_available: "",
    draws_allowed: "",
    last_term_advance_date_allowed: "",
    sofr_on_start: "",
    sofr_resets: "first_of_quarter",
    spread_on_start: "",
    length_of_year_days: "360",
    interest_payment_schedule: "quarterly",
    notes: "",
  });

  useEffect(() => {
    if (loanNote) {
      setFormData({
        loan_from: loanNote.loan_from || "",
        loan_to: loanNote.loan_to || "",
        loan_signed_on: loanNote.loan_signed_on || "",
        loan_agreement_url: loanNote.loan_agreement_url || "",
        total_funds_available: loanNote.total_funds_available?.toString() || "",
        draws_allowed: loanNote.draws_allowed?.toString() || "",
        last_term_advance_date_allowed: loanNote.last_term_advance_date_allowed || "",
        sofr_on_start: loanNote.sofr_on_start?.toString() || "",
        sofr_resets: loanNote.sofr_resets || "first_of_quarter",
        spread_on_start: loanNote.spread_on_start?.toString() || "",
        length_of_year_days: loanNote.length_of_year_days?.toString() || "360",
        interest_payment_schedule: loanNote.interest_payment_schedule || "quarterly",
        notes: loanNote.notes || "",
      });
      // Fetch and display the original filename from storage metadata
      if (loanNote.loan_agreement_url) {
        setUploadedFileName('Loan Agreement')
      }
    }
  }, [loanNote]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${lenderProfileId}-${Date.now()}.${fileExt}`;
      const filePath = `loan-agreements/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('loan-agreements')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
          metadata: {
            originalFileName: file.name
          }
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('loan-agreements')
        .getPublicUrl(filePath);

      setFormData({ ...formData, loan_agreement_url: publicUrl });
      setUploadedFileName(file.name);
      toast({ title: "File uploaded successfully" });
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Error",
        description: "Failed to upload file",
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        lender_profile_id: lenderProfileId,
        loan_from: formData.loan_from,
        loan_to: formData.loan_to,
        loan_signed_on: formData.loan_signed_on,
        loan_agreement_url: formData.loan_agreement_url || null,
        total_funds_available: parseFloat(formData.total_funds_available),
        draws_allowed: parseInt(formData.draws_allowed),
        last_term_advance_date_allowed: formData.last_term_advance_date_allowed || null,
        sofr_on_start: parseFloat(formData.sofr_on_start),
        sofr_resets: formData.sofr_resets,
        spread_on_start: parseFloat(formData.spread_on_start),
        length_of_year_days: parseInt(formData.length_of_year_days),
        interest_payment_schedule: formData.interest_payment_schedule,
        notes: formData.notes || null,
      };

      if (loanNote) {
        const { error } = await supabase
          .from("loan_notes")
          .update(data)
          .eq("id", loanNote.id);

        if (error) throw error;
        toast({ title: "Loan note updated successfully" });
      } else {
        const { error } = await supabase
          .from("loan_notes")
          .insert(data);

        if (error) throw error;
        toast({ title: "Loan note created successfully" });
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving loan note:", error);
      toast({
        title: "Error",
        description: "Failed to save loan note",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="loan_from">Loan from</Label>
          <Select value={formData.loan_from} onValueChange={(value) => setFormData({ ...formData, loan_from: value })} required>
            <SelectTrigger>
              <SelectValue placeholder="Select loan from" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EA PIIF">EA PIIF</SelectItem>
              <SelectItem value="Figabl">Figabl</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="loan_to">Loan to</Label>
          <Select value={formData.loan_to} onValueChange={(value) => setFormData({ ...formData, loan_to: value })} required>
            <SelectTrigger>
              <SelectValue placeholder="Select loan to" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ABL7">ABL7</SelectItem>
              <SelectItem value="ABL8">ABL8</SelectItem>
              <SelectItem value="Figabl">Figabl</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="loan_signed_on">Loan signed on</Label>
          <Input
            id="loan_signed_on"
            type="date"
            value={formData.loan_signed_on}
            onChange={(e) => setFormData({ ...formData, loan_signed_on: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="loan_agreement_url">Loan Agreement</Label>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                id="loan_agreement_file"
                type="file"
                onChange={handleFileUpload}
                disabled={uploadingFile}
                className="hidden"
                accept=".pdf,.doc,.docx"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('loan_agreement_file')?.click()}
                disabled={uploadingFile}
                className="w-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingFile ? "Uploading..." : "Upload Agreement"}
              </Button>
            </div>
            {formData.loan_agreement_url && (
              <p className="text-sm text-muted-foreground truncate">
                File uploaded: {uploadedFileName}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="total_funds_available">Total funds available</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input
              id="total_funds_available"
              type="text"
              className="pl-7"
              value={formData.total_funds_available ? parseFloat(formData.total_funds_available.replace(/,/g, '')).toLocaleString('en-US') : ''}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, '');
                if (value === '' || !isNaN(parseFloat(value))) {
                  setFormData({ ...formData, total_funds_available: value });
                }
              }}
              required
            />
          </div>
        </div>
        <div>
          <Label htmlFor="draws_allowed">Draws (term advances) allowed</Label>
          <Input
            id="draws_allowed"
            type="number"
            value={formData.draws_allowed}
            onChange={(e) => setFormData({ ...formData, draws_allowed: e.target.value })}
            required
          />
        </div>
      </div>

      <div>
        <Label htmlFor="last_term_advance_date_allowed">Last term advance date allowed</Label>
        <Input
          id="last_term_advance_date_allowed"
          type="date"
          value={formData.last_term_advance_date_allowed}
          onChange={(e) => setFormData({ ...formData, last_term_advance_date_allowed: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="sofr_on_start">SOFR on start (%)</Label>
          <Input
            id="sofr_on_start"
            type="number"
            step="0.01"
            value={formData.sofr_on_start}
            onChange={(e) => setFormData({ ...formData, sofr_on_start: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="sofr_resets">SOFR resets</Label>
          <Select value={formData.sofr_resets} onValueChange={(value) => setFormData({ ...formData, sofr_resets: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="first_of_month">First of Month</SelectItem>
              <SelectItem value="first_of_quarter">First of Quarter</SelectItem>
              <SelectItem value="never">Never</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="spread_on_start">Spread on start (%)</Label>
          <Input
            id="spread_on_start"
            type="number"
            step="0.01"
            value={formData.spread_on_start}
            onChange={(e) => setFormData({ ...formData, spread_on_start: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="length_of_year_days">Length of year (days)</Label>
          <Input
            id="length_of_year_days"
            type="number"
            value={formData.length_of_year_days}
            onChange={(e) => setFormData({ ...formData, length_of_year_days: e.target.value })}
            required
          />
        </div>
      </div>

      <div>
        <Label htmlFor="apr_on_start">APR% on Start</Label>
        <Input
          id="apr_on_start"
          disabled
          value={
            formData.sofr_on_start && formData.spread_on_start && formData.length_of_year_days && formData.interest_payment_schedule
              ? `${(parseFloat(formData.sofr_on_start) + parseFloat(formData.spread_on_start)).toFixed(2)}% (${formData.length_of_year_days} days, Paid ${formData.interest_payment_schedule.charAt(0).toUpperCase() + formData.interest_payment_schedule.slice(1)})`
              : ''
          }
          className="bg-muted"
        />
      </div>

      <div>
        <Label htmlFor="interest_payment_schedule">Interest payment schedule</Label>
        <Select value={formData.interest_payment_schedule} onValueChange={(value) => setFormData({ ...formData, interest_payment_schedule: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
            <SelectItem value="annually">Bi-Annually</SelectItem>
            <SelectItem value="annually">Annually</SelectItem>
            <SelectItem value="annually">Compounds Annually</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Saving..." : loanNote ? "Update Loan Note" : "Create Loan Note"}
        </Button>
      </div>
    </form>
  );
};
